import React, { useState, useEffect } from 'react';
import { Users, Award, Lightbulb, TrendingUp, Target, Eye } from 'lucide-react';
import { aboutContent as mockAbout, siteSettings as mockSettings } from '../data/mock';
import { AboutOfficeCard, OFFICE_CARDS } from '../components/about/AboutOfficeCard';
import { contentAPI } from '../services/api';
import AnimatedCounter from '../components/AnimatedCounter';

const About = () => {
  const [aboutContent, setAboutContent] = useState(mockAbout);
  const [siteSettings, setSiteSettings] = useState(mockSettings);

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.allSettled([contentAPI.get('about'), contentAPI.get('site_settings')]).then(([a, s]) => {
      if (a.status === 'fulfilled' && a.value.data && Object.keys(a.value.data).length) setAboutContent(a.value.data);
      if (s.status === 'fulfilled' && s.value.data && Object.keys(s.value.data).length) {
        // Use database data BUT always use hardcoded offices from mockSettings
        setSiteSettings({
          ...s.value.data,
          offices: mockSettings.offices  // ALWAYS use mock offices, ignore database
        });
      }
    });
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Who We Are Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Who <span className="text-emerald-600">We Are</span>
              </h1>
              <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
                <p>
                  We are a leading Renewable Energy consulting firm dedicated to helping businesses transition to clean, sustainable energy solutions. With deep expertise in regulatory compliance, legal frameworks, and technical consulting, we serve as trusted advisors to developers, EPC companies, and corporations across India.
                </p>
                <p>
                  Our comprehensive suite of services spans regulatory consultancy, legal advisory, and renewable energy project consulting. We provide tailored solutions that address the unique challenges and opportunities in India's rapidly evolving renewable energy sector.
                </p>
                <p>
                  Our team of experts brings together decades of combined experience in energy regulation, legal practice, and project development. We pride ourselves on delivering results that exceed client expectations while contributing to India's renewable energy goals.
                </p>
              </div>
            </div>

            {/* Right: Value Cards Grid */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-3xl p-8 text-center shadow-lg transform hover:-translate-y-2 transition-all duration-300">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <Users className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Team</h3>
                <p className="text-gray-600 text-sm">Decades of combined experience</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8 text-center shadow-lg transform hover:-translate-y-2 transition-all duration-300">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <Award className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Proven Track Record</h3>
                <p className="text-gray-600 text-sm">250+ MW delivered</p>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-3xl p-8 text-center shadow-lg transform hover:-translate-y-2 transition-all duration-300">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <Lightbulb className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Innovative Solutions</h3>
                <p className="text-gray-600 text-sm">Creative approach to challenges</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8 text-center shadow-lg transform hover:-translate-y-2 transition-all duration-300">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <TrendingUp className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Results Driven</h3>
                <p className="text-gray-600 text-sm">Exceeding client expectations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vision Card */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl border-t-8 border-emerald-600 transform hover:-translate-y-2 transition-all duration-300">
              <div className="p-10">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                  <Eye className="w-10 h-10 text-emerald-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Vision</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  To be India's most trusted and comprehensive renewable energy consulting partner, driving the nation's transition to clean energy. We envision a future where every business has access to expert guidance for adopting renewable energy solutions, contributing to India's ambitious clean energy targets and global climate commitments.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We strive to be recognized as thought leaders in renewable energy consulting, setting industry standards for regulatory compliance, legal advisory, and project execution excellence.
                </p>
              </div>
            </div>

            {/* Mission Card */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl border-t-8 border-blue-600 transform hover:-translate-y-2 transition-all duration-300">
              <div className="p-10">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <Target className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  To empower businesses with tailored renewable energy solutions through expert consulting, regulatory guidance, and legal advisory services. We are committed to simplifying the complexities of renewable energy adoption, ensuring regulatory compliance, and maximizing value for our clients.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our mission is to accelerate renewable energy deployment across India by providing end-to-end consulting support that addresses technical, regulatory, legal, and commercial challenges, making clean energy accessible and profitable for all.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Our Core <span className="text-emerald-600">Values</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-8 text-center transform hover:-translate-y-2 transition-all duration-300 shadow-lg">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <Award className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Excellence</h3>
              <p className="text-gray-600 leading-relaxed">
                Delivering superior quality in every engagement
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 text-center transform hover:-translate-y-2 transition-all duration-300 shadow-lg">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <Users className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Integrity</h3>
              <p className="text-gray-600 leading-relaxed">
                Transparent and ethical business practices
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-8 text-center transform hover:-translate-y-2 transition-all duration-300 shadow-lg">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <Lightbulb className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Innovation</h3>
              <p className="text-gray-600 leading-relaxed">
                Creative solutions for complex challenges
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 text-center transform hover:-translate-y-2 transition-all duration-300 shadow-lg">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <TrendingUp className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Client Success</h3>
              <p className="text-gray-600 leading-relaxed">
                Committed to our clients' long-term success
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-emerald-700 via-teal-700 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Choose <span className="text-yellow-300">Us?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-10 shadow-xl hover:bg-white/15 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-6">Deep Expertise</h3>
              <p className="text-white/90 leading-relaxed">
                Comprehensive knowledge of regulatory frameworks, legal requirements, and technical aspects of renewable energy projects
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-10 shadow-xl hover:bg-white/15 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-6">End-to-End Support</h3>
              <p className="text-white/90 leading-relaxed">
                From initial consultation to project commissioning, we provide complete support at every stage of your renewable energy journey
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-10 shadow-xl hover:bg-white/15 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-6">Proven Results</h3>
              <p className="text-white/90 leading-relaxed">
                Track record of successful project implementations and satisfied clients across diverse industry segments
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-lg text-gray-600">Key milestones in our growth story</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.milestones.map((milestone) => (
              <div
                key={`${milestone.year}-${milestone.title}`}
                className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-xl p-6 text-center transform hover:-translate-y-2 transition-all duration-300"
              >
                <div className="text-4xl font-bold text-emerald-600 mb-3">{milestone.year}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-center text-white">
            <div>
              <div className="text-6xl font-bold mb-2">
                <AnimatedCounter end={siteSettings.counters.projectsMW} suffix="+" />
              </div>
              <p className="text-2xl font-medium">MW Projects Delivered</p>
              <p className="text-emerald-100 mt-2">Across India</p>
            </div>
            <div>
              <div className="text-6xl font-bold mb-2">
                <AnimatedCounter end={siteSettings.counters.ppasDelivered} suffix="+" />
              </div>
              <p className="text-2xl font-medium">PPAs Delivered</p>
              <p className="text-emerald-100 mt-2">Successfully Executed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Offices</h2>
            <p className="text-lg text-gray-600">Serving clients across India</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {OFFICE_CARDS.map(cfg => <AboutOfficeCard key={cfg.key} office={siteSettings.offices?.[cfg.key]} config={cfg} />)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
